import { Button, Card, CardContent, Link, Stack, Typography } from '@mui/material';
import { ContentCopyOutlined, EmailOutlined, OpenInNewOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { FunctionComponent, useContext, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { SetupInstructionsDialog } from '../shared-components/components/setup-instructions-dialog';
import BusinessCentralService from '../shared-services/business-central/business-central.service';
import { BusinessCentralLinkPurpose, IBusinessCentralStatus } from '../shared-services/business-central/business-central.model';
import { BusinessCentralAppSteps } from './business-central-app-steps';

const cardSx = { borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none' };

/**
 * Connecting Business Central, built like the AWS panel: the people who can grant access are
 * usually a Microsoft administrator and a Business Central administrator, not our user, so the
 * panel is built around handing the job over. The one link (the button, the email, or the copied
 * instructions) grants access and then asks for a sign-in, and it is that sign-in, never anything
 * typed here, that tells us which Microsoft tenant is the organisation's. Microsoft sends whoever
 * uses the link to a public page, so it works for an administrator who is not a user of ours.
 */
export const BusinessCentralConnectPanel: FunctionComponent<{
  status: IBusinessCentralStatus,
  isAdmin: boolean,
  onUpdated: (status: IBusinessCentralStatus) => void,
}> = ({ status, isAdmin, onUpdated }) => {
  const businessCentralService = new BusinessCentralService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [openingPurpose, setOpeningPurpose] = useState<BusinessCentralLinkPurpose | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ email: string, name: string }>({ email: '', name: '' });

  const lastSent = status.lastInstructionsSent;
  const clientId = status.clientId ?? '';

  const errorSnackbar = (err: any, fallback: string) => userNotificationsContext.openSnackbar({
    open: true,
    message: err?.response?.data?.message || fallback,
    backgroundColor: 'text.primary',
  });

  const copy = (text: string, successMessage: string) => {
    navigator.clipboard.writeText(text)
      .then(() => userNotificationsContext.openSnackbar({ open: true, message: successMessage, backgroundColor: 'text.primary' }))
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'Could not copy; select the text by hand', backgroundColor: 'text.primary' }));
  };

  // A new tab, so this page stays open and picks up the connection when it is focused again. The
  // tab is opened during the click and pointed at Microsoft once the link arrives: opened after
  // the request instead, a popup blocker would stop it. Blocked anyway, this tab goes instead.
  const openLink = (purpose: BusinessCentralLinkPurpose) => {
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setOpeningPurpose(purpose);
    businessCentralService.createLink(purpose, 'button')
      .then(link => {
        if (tab && !tab.closed) tab.location.href = link.url;
        else window.location.assign(link.url);
      })
      .catch(err => {
        if (tab) tab.close();
        errorSnackbar(err, 'The link could not be created. Please try again.');
      })
      .finally(() => setOpeningPurpose(null));
  };

  const copyInstructions = () => {
    setIsCopying(true);
    businessCentralService.createLink('consent', 'copy')
      .then(link => {
        if (link.instructionsText) copy(link.instructionsText, 'Instructions copied. Paste them into your ticket or message.');
      })
      .catch(err => errorSnackbar(err, 'The instructions could not be created. Please try again.'))
      .finally(() => setIsCopying(false));
  };

  const openDialog = (resend: boolean) => {
    setPrefill(resend && lastSent ? { email: lastSent.email, name: lastSent.name ?? '' } : { email: '', name: '' });
    setDialogOpen(true);
  };

  if (!isAdmin) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        Ask an admin in your organisation to connect Business Central.
      </Typography>
    );
  }

  return (
    <Card variant='outlined' sx={cardSx}>
      <CardContent>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant='h6' sx={{ color: 'text.primary' }}>Connect Business Central</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
              Business Central is read with its own read-only access, never with your password. A Microsoft
              administrator grants it once, and a Business Central administrator allows it inside Business
              Central. If that isn&apos;t you, email them the instructions.
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Step 1: grant access in Microsoft</Typography>
            {/* Flex gap, not spacing: wrapped buttons start aligned with a gap above them. */}
            <Stack direction='row' flexWrap='wrap' useFlexGap gap={2} alignItems='center'>
              <LoadingButton
                variant='contained'
                loading={openingPurpose === 'consent'}
                startIcon={<OpenInNewOutlined fontSize='small' />}
                onClick={() => openLink('consent')}
              >
                <Typography sx={{ pl: 0.5 }}>Connect with Microsoft</Typography>
              </LoadingButton>
              <Button variant='outlined' startIcon={<EmailOutlined fontSize='small' />} onClick={() => openDialog(false)}>
                <Typography sx={{ pl: 0.5 }}>Email instructions</Typography>
              </Button>
              <LoadingButton
                variant='text'
                loading={isCopying}
                startIcon={<ContentCopyOutlined fontSize='small' />}
                onClick={copyInstructions}
              >
                <Typography sx={{ pl: 0.5 }}>Copy instructions</Typography>
              </LoadingButton>
            </Stack>
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
              Connect with Microsoft is for a Microsoft administrator (for example a Global Administrator or
              Application Administrator). After granting access you are asked to sign in once more: that
              sign-in is how we know which Microsoft tenant is yours.
            </Typography>
            {lastSent && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Instructions sent to {lastSent.email} on {moment(lastSent.sentAt).format('D MMM YYYY')}
                {lastSent.sentByName ? ` by ${lastSent.sentByName}` : ''}.{' '}
                <Link component='button' type='button' onClick={() => openDialog(true)} sx={{ verticalAlign: 'baseline' }}>
                  Resend
                </Link>
              </Typography>
            )}
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Access already granted for your organisation?{' '}
              <Link component='button' type='button' onClick={() => openLink('sign-in')} sx={{ verticalAlign: 'baseline' }}>
                Sign in with a Microsoft work account to link it
              </Link>
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Step 2: allow the application in Business Central</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
              A Business Central administrator does this once. Microsoft access alone is not enough, because
              Business Central keeps its own list of what each application may read.
            </Typography>
            <BusinessCentralAppSteps clientId={clientId} entraApplicationsUrl={status.entraApplicationsUrl} />
          </Stack>
        </Stack>
      </CardContent>

      <SetupInstructionsDialog<IBusinessCentralStatus>
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSent={onUpdated}
        send={recipient => businessCentralService.sendInstructions(recipient)}
        adminLabel='Microsoft administrator'
        description={'They will receive what the access can and cannot do (it reads posted purchase invoices and your '
          + 'chart of accounts), a link that grants access and links your Microsoft tenant, and the steps for '
          + 'Business Central. The link works once and expires after seven days. Their reply comes to your email address.'}
        noteMax={status.instructionsNoteMaxLength}
        initialEmail={prefill.email}
        initialName={prefill.name}
      />
    </Card>
  );
};
